import { QueryCache, QueryClient } from '@tanstack/react-query'

import { isApiError, type ApiError } from './client'

/** The cache key for "who am I". Exported so a 401 anywhere can reset it. */
export const ME_QUERY_KEY = ['auth', 'me'] as const

/**
 * Build the app's query client.
 *
 * Two decisions worth stating:
 *
 * - **Do not retry a refusal.** Retrying a 401/403/404/422 cannot change the
 *   answer; it only delays the error the user is waiting for. Transport
 *   failures and 5xx get two more goes.
 * - **A 401 from *any* query signs the app out.** Sessions expire while a tab
 *   sits open, and without this the user's next click fails silently against a
 *   dead cookie. Writing `null` into {@link ME_QUERY_KEY} makes the shell fall
 *   back to SignIn on the next render.
 */
export function createQueryClient(): QueryClient {
  const queryCache = new QueryCache({
    onError: (error) => {
      if (isApiError(error) && error.isUnauthenticated) {
        queryClient.setQueryData(ME_QUERY_KEY, null)
      }
    },
  })

  const queryClient = new QueryClient({
    queryCache,
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (isApiError(error) && !error.isNetworkError && error.status < 500) return false
          return failureCount < 2
        },
      },
      mutations: {
        retry: false,
      },
    },
  })

  return queryClient
}

/** Narrow an unknown query/mutation error to {@link ApiError}, or `null`. */
export function asApiError(error: unknown): ApiError | null {
  return isApiError(error) ? error : null
}
